import type { CapturedEventInput } from '@nextmar/snowplow-sdk';
import { ProviderType, type TrackingEvent } from '../types';
import { generateEventId } from '../utils/eventIdGenerator';
import { filterSensorsBusinessProperties } from '../utils/sensorsProperties';
import type { ManualOperation } from '../reliability/manualAnalyticsTypes';
import { getEventAdapter } from './eventAdapters';

/** Sensors 与 SP 的共有分支必须显式声明，不能按数组位置盲配。 */
const SHARED_MANUAL_EVENTS: Readonly<Record<string, readonly string[]>> = {
  'checkout.click': ['CartCheckOut_Click'],
  'checkout.result': ['CartCheckOut_Result'],
  'purchase.success': ['PlaceOrderSuccessPage_View'],
};
/** 适配结果只划分手动镜像及既有独立 SP 输出，不接管广告。 */
export interface ManualAdaptationResult {
  operations: readonly ManualOperation[];
  standaloneSnowplow: readonly TrackingEvent[];
}

/**
 * 保留输入已有的原始非空关联 ID。
 * @param event 业务或已适配事件。
 * @returns 原值或生成一次的 ID，不用 ID 合并真实事件。
 */
export function manualEventId(event: TrackingEvent): string {
  const original = event?.properties?.sensors_event_id;
  if (typeof original === 'string' && original.trim()) return original;
  return typeof event?.eventId === 'string' && event.eventId.trim()
    ? event.eventId
    : generateEventId();
}

/**
 * 将原有 SP 映射转为 SDK 输入，手动同名 PageView 走原生协议。
 * @param event 原有 SP 事件，事件属性由 SDK 白名单清理。
 * @returns 独立的发生输入，不读取 Sensors 身份。
 */
export function manualMirrorInput(event: TrackingEvent): CapturedEventInput {
  const properties = structuredClone(event.properties ?? {});
  if (event.eventName === '$pageview') return { kind: 'pageView', properties };
  return {
    kind: 'track',
    eventName: event.eventName ?? '',
    properties,
    options: { sensorsEventId: manualEventId(event), omitPixel: event.options?.omitPixel },
  };
}

/**
 * 在发生时适配手动埋点，明确保留适配器的额外 SP 分支。
 * @param event 原业务事件；适配器调用可只提供关联 ID。
 * @param adaptionData 原适配数据，两个渠道使用独立副本。
 * @param adapterType 既有适配器名称；共同输出需在固定映射中声明。
 * @returns 操作与独立 SP 输出；无法对应时抛错，由登记入口报告接入失败。
 */
export function adaptManualReporting(
  event: TrackingEvent,
  adaptionData: unknown,
  adapterType?: string,
): ManualAdaptationResult {
  const eventId = manualEventId(event);
  const original = structuredClone({ ...event, eventId });
  const adapter = adapterType ? getEventAdapter(adapterType) : undefined;
  if (adapterType && !adapter) throw new Error('unknown_manual_adapter');
  /** @param type 旧渠道。@returns 各自独立的旧适配结果，不修改原始输入。 */
  function adapt(type: ProviderType): TrackingEvent[] {
    const adapted = adapter ? adapter(structuredClone(adaptionData), type, eventId) : original;
    return structuredClone(adapted ? (Array.isArray(adapted) ? adapted : [adapted]) : []);
  }
  const sensors = adapt(ProviderType.SENSORS);
  const snowplow = adapt(ProviderType.SNOWPLOW);
  if (!sensors.length) return { operations: [], standaloneSnowplow: snowplow };
  const remaining = [...snowplow];
  const operations = sensors.map((sensor): ManualOperation => {
    const name = sensor.eventName;
    if (adapterType && !SHARED_MANUAL_EVENTS[adapterType]?.includes(name ?? ''))
      throw new Error('undeclared_manual_adapter_mapping');
    const index = remaining.findIndex((candidate) => candidate.eventName === name);
    if (index < 0) throw new Error('missing_manual_mirror_mapping');
    const mirror = remaining.splice(index, 1)[0];
    const correlation = manualEventId({ ...sensor, eventId: sensor.eventId ?? eventId });
    return {
      kind: 'track',
      eventName: name ?? '',
      properties: {
        ...filterSensorsBusinessProperties(sensor.properties ?? {}),
        sensors_event_id: correlation,
      },
      mirrorInput: manualMirrorInput({
        ...mirror,
        eventId: correlation,
        properties: { ...mirror.properties, sensors_event_id: correlation },
      }),
    };
  });
  return { operations, standaloneSnowplow: remaining };
}
